// The time-of-day clock (decision 0048: a full day-night cycle): sim state in game hours with a rate, stepped on the
// sim's fixed step, with marks (dawn, day, dusk, night: the game names them) it reports crossing. Pure data and
// functions: no wall clock, no randomness, so the same steps give the same hours, and the state is JSON for saves.
// The game owns every value (the day's length in real minutes, the marks, the start).

/** A named time of day the clock reports crossing (`at`: hours, 0 to 24). */
export interface ClockMark {
  id: string;
  at: number;
}

/** The clock's values: how many real minutes a whole day takes at rate 1, and its marks. */
export interface ClockConfig {
  /** Real minutes for 24 game hours at rate 1. */
  dayMinutes: number;
  /** The marks, any order; each `at` in [0, 24). */
  marks: readonly ClockMark[];
}

/** The clock's state (saved). */
export interface ClockState {
  /** Hours into the day, [0, 24). */
  hours: number;
  /** Whole days since the start. */
  day: number;
  /** A multiplier on the day's pace (0 stops the clock). */
  rate: number;
}

/** A mark crossed in a step: which, and on which day. */
export interface CrossedMark {
  id: string;
  at: number;
  day: number;
}

/** A fresh clock at `hours` on day 0 (rate 1 unless given). */
export function createClockState(hours: number, rate = 1): ClockState {
  if (!Number.isFinite(hours) || !Number.isFinite(rate) || rate < 0) throw new Error(`@rpg/world clock: bad start ${hours} or rate ${rate}`);
  return { hours: wrapHours(hours), day: 0, rate };
}

/** Throws on a config that can't work (no day length, marks outside the day, two marks with one id). */
export function validateClock(c: ClockConfig): void {
  if (!(c.dayMinutes > 0) || !Number.isFinite(c.dayMinutes)) throw new Error(`@rpg/world clock: dayMinutes must be > 0 (got ${c.dayMinutes})`);
  const seen = new Set<string>();
  for (const m of c.marks) {
    if (!(m.at >= 0 && m.at < 24)) throw new Error(`@rpg/world clock: mark "${m.id}" at ${m.at} is outside [0, 24)`);
    if (seen.has(m.id)) throw new Error(`@rpg/world clock: two marks named "${m.id}"`);
    seen.add(m.id);
  }
}

/** Game hours per sim second at a rate. */
export function hoursPerSecond(c: ClockConfig, rate: number): number {
  return (24 / (c.dayMinutes * 60)) * rate;
}

/** Hours since day 0's midnight. */
export function totalHours(s: Readonly<ClockState>): number {
  return s.day * 24 + s.hours;
}

/** Wrap any hour count into [0, 24). */
export function wrapHours(h: number): number {
  const w = h % 24;
  return w < 0 ? w + 24 : w;
}

/** The marks crossed going from total hour `from` (exclusive) to `to` (inclusive), in order. */
export function marksBetween(marks: readonly ClockMark[], from: number, to: number): CrossedMark[] {
  if (!(to > from)) return [];
  const sorted = [...marks].sort((a, b) => a.at - b.at);
  const out: CrossedMark[] = [];
  for (let day = Math.floor(from / 24); day * 24 <= to; day++)
    for (const m of sorted) {
      const t = day * 24 + m.at;
      if (t > from && t <= to) out.push({ id: m.id, at: m.at, day });
    }
  return out;
}

/** Move the clock on by `dt` sim seconds (mutates `s`); returns the marks crossed, in order. */
export function stepClock(s: ClockState, dt: number, c: ClockConfig): CrossedMark[] {
  const from = totalHours(s);
  const to = from + Math.max(0, dt) * hoursPerSecond(c, s.rate);
  setTotal(s, to);
  return marksBetween(c.marks, from, to);
}

/** Jump the clock to `hours` (forward to the next such hour, never back), returning the marks crossed on the way. */
export function advanceTo(s: ClockState, hours: number, c: ClockConfig): CrossedMark[] {
  const from = totalHours(s);
  let to = Math.floor(from / 24) * 24 + wrapHours(hours);
  if (to < from) to += 24;
  setTotal(s, to);
  return marksBetween(c.marks, from, to);
}

/** The mark whose period holds `hours`: the last mark at or before it, wrapping past midnight. */
export function periodAt(marks: readonly ClockMark[], hours: number): string | null {
  if (!marks.length) return null;
  const h = wrapHours(hours);
  const sorted = [...marks].sort((a, b) => a.at - b.at);
  let cur = sorted[sorted.length - 1]!;
  for (const m of sorted) if (m.at <= h) cur = m;
  return cur.id;
}

function setTotal(s: ClockState, total: number): void {
  s.day = Math.floor(total / 24);
  s.hours = total - s.day * 24;
}
